import { envLogger } from "@/utils/feature-flags";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { BasketIcon } from "@funds/components/baskets/icon";
import {
  FUND_BASKET_TENURE_OPTIONS,
  FUND_BASKET_STRATEGY_OPTIONS,
} from "creso-core/constants";
import {
  useFetchFundBasketById,
  useFetchSchemesByIsin,
  useFundBaskets,
} from "creso-core/hooks";
import { FundBasketSummary, Scheme, SchemeType } from "creso-core/types";
import {
  ChevronRightIcon,
  Loader2Icon,
  ShoppingBasketIcon,
} from "lucide-react";
import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  Badge,
  Button,
  cn,
  CurrencyInput,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  SearchInput,
} from "web-design-system";

export type CollectionType = "basket" | "proposal";

interface BasketPickerProps<T extends SchemeType> {
  schemeType: T;
  onImport: (
    result: {
      scheme: Scheme<T>;
      amount: string;
      collection: {
        name: string;
        id: number;
        type: "basket" | "proposal";
        metadata?: Record<string, unknown>;
      };
    }[]
  ) => Promise<void>;
  basketId?: number | null;
  onCancel?: () => void;
  defaultAmount?: string;
}

function BasketAmountRow({
  isSelected,
  amount,
  onAmountChange,
  onImport,
}: {
  isSelected: boolean;
  amount: string;
  onAmountChange: (v: string) => void;
  onImport: () => Promise<void>;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isImporting, setIsImporting] = useState(false);

  const handleImport = useCallback(async () => {
    try {
      setIsImporting(true);
      await onImport();
    } finally {
      setIsImporting(false);
    }
  }, [onImport]);

  useEffect(() => {
    if (isSelected) {
      containerRef.current?.querySelector<HTMLInputElement>("input")?.focus();
    }
  }, [isSelected]);

  return (
    <div
      ref={containerRef}
      aria-hidden={!isSelected}
      className={cn(
        "flex flex-row px-0.5 justify-between items-center overflow-hidden transition-[height,opacity] duration-300 ease-out",
        isSelected ? "h-12 opacity-100" : "h-0 opacity-0 pointer-events-none"
      )}
    >
      <CurrencyInput
        onValueChange={(v) => onAmountChange(v ?? "")}
        value={amount}
        className="w-fit"
        placeholder="₹ Amount"
      />
      <Button
        onClick={handleImport}
        size="sm"
        disabled={!amount || isImporting}
        type="button"
      >
        {isImporting && <Loader2Icon className="size-4 animate-spin" />}
        Import
      </Button>
    </div>
  );
}

export function BasketPicker<T extends SchemeType = "all">({
  schemeType,
  onImport,
  basketId,
  onCancel,
  defaultAmount,
}: BasketPickerProps<T>) {
  const isEditing = basketId != null;
  const { data: baskets, isLoading } = useFundBaskets();
  const [basketSearch, setBasketSearch] = useState("");

  const [selectedBasketId, setSelectedBasketId] = useState<number | null>(
    basketId ?? null
  );

  const allBaskets: FundBasketSummary[] = useMemo(() => {
    if (!baskets) return [];
    return [...baskets.custom_baskets, ...baskets.curated_baskets];
  }, [baskets]);

  const filteredBaskets = useMemo(() => {
    if (isEditing) {
      return allBaskets.filter((b) => b.id === basketId);
    }
    const q = basketSearch.trim().toLowerCase();
    if (!q) return allBaskets;
    return allBaskets.filter((b) => b.name.toLowerCase().includes(q));
  }, [allBaskets, basketSearch, isEditing, basketId]);

  const fetchSchemesByIsin = useFetchSchemesByIsin<T>(schemeType);

  const fetchFundBasketById = useFetchFundBasketById();

  const handleImportBasket = useCallback(
    async (basketSummary: FundBasketSummary, amount: number) => {
      try {
        const basket = await fetchFundBasketById(basketSummary.id);
        const isins = basket.funds.map((f) => f.scheme_isin);
        const schemes = await fetchSchemesByIsin(isins);
        await onImport(
          schemes.map((s) => {
            const fundAllocation =
              basket.funds.find((f) => f.scheme_isin === s.scheme_isin)
                ?.allocation_percentage ?? "0";
            return {
              scheme: s,
              amount: Math.round(
                amount * (Number(fundAllocation) / 100)
              ).toString(),
              collection: {
                name: basket.name,
                id: basket.id,
                type: "basket",
                metadata: {
                  strategy: basket.strategy,
                },
              },
            };
          })
        );
      } catch (e) {
        envLogger.error(e);
        toast.error("Failed to import schemes from basket");
      }
    },
    [fetchSchemesByIsin, onImport, fetchFundBasketById]
  );

  const [amount, setAmount] = useState(defaultAmount);

  return (
    <Collapsible
      defaultOpen={isEditing}
      className="border w-full group border-border rounded-md bg-linear-to-r from-tag-yellow to-background"
    >
      <CollapsibleTrigger asChild>
        <div className="flex items-center gap-2 px-4 py-3">
          <ShoppingBasketIcon className="size-5 stroke-tag-yellow-foreground" />
          <span className="text-sm">
            {isEditing
              ? "Set basket total amount"
              : "Add schemes from a curated basket"}
          </span>
          {isEditing && onCancel ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="ml-auto text-xs h-auto p-0"
              onClick={(e) => {
                e.stopPropagation();
                onCancel();
              }}
            >
              Cancel
            </Button>
          ) : (
            <ChevronRightIcon className="size-4 ml-auto group-data-[state=open]:rotate-90" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="max-h-70 relative overflow-auto">
        <div className="flex flex-col gap-3 px-4 pb-4">
          {!isEditing && (
            <div className="sticky top-0 pt-1 z-2 w-full gap-3 flex flex-col bg-linear-to-r from-tag-yellow to-background">
              <SearchInput
                className="pl-9 w-1/2"
                placeholder="Search Baskets"
                value={basketSearch}
                autoFocus
                onChange={(e) => setBasketSearch(e.target.value)}
              />
              <div className="h-px bg-border" />
            </div>
          )}
          <div className="flex flex-col">
            {filteredBaskets.map((basket) => {
              const tenure = FUND_BASKET_TENURE_OPTIONS.find(
                (f) => f.value === basket.tenure
              )?.label;
              const strategy = FUND_BASKET_STRATEGY_OPTIONS.find(
                (f) => f.value === basket.strategy
              )?.label;
              const description = (
                <>
                  {tenure} <span className="text-muted-foreground">·</span>{" "}
                  {strategy}
                </>
              );
              const logos = Array.from(
                new Set(basket.funds_summary.map((f) => f.provider_company_id))
              ).slice(0, 3);
              const extraFundCount = Math.max(
                0,
                basket.scheme_count - logos.length
              );

              return (
                <div
                  key={basket.id}
                  onClick={() => setSelectedBasketId(basket.id)}
                  className={cn(
                    "flex flex-col rounded px-1 py-1.5 gap-1 hover:bg-tag-yellow"
                  )}
                >
                  <div className="flex flex-row items-center justify-between w-full gap-2">
                    <BasketIcon size="sm" strategy={basket.strategy} />
                    <div className="min-w-0 flex items-start flex-col flex-1">
                      <div className="text-sm font-semibold">{basket.name}</div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                        <span>{description}</span>
                        {basket.basket_type === "curated" ? (
                          <Badge
                            variant="custom"
                            className="py-0.5 px-1.5 text-xxs font-medium text-tag-yellow-foreground bg-tag-yellow"
                          >
                            Curated by Creso
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <div className="-space-x-1.5 flex flex-row">
                        {logos.map((logo) => (
                          <AmcLogo
                            key={logo}
                            className="border border-border"
                            size="sm"
                            amc_code={logo}
                          />
                        ))}
                        {extraFundCount > 0 ? (
                          <span className="rounded-md z-1 border border-border bg-background size-8 flex items-center justify-center text-xxs font-medium text-muted-foreground">
                            +{extraFundCount}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  <BasketAmountRow
                    isSelected={selectedBasketId === basket.id}
                    amount={amount ?? ""}
                    onAmountChange={setAmount}
                    onImport={() => handleImportBasket(basket, Number(amount))}
                  />
                </div>
              );
            })}
            {filteredBaskets.length === 0 && !isLoading ? (
              <p className="text-center text-xs text-muted-foreground py-4">
                No baskets match your search.
              </p>
            ) : null}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
